import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import GameByStatusCards from '../ui/gameStatusCards/gameByStatusCards';
import { getGameBySectionId } from '../../features/game/gameSlice';
import { useAuth } from '../../contexts/authContext';
import BSCircularProgress from '../ui/progress/circularProgress';
import { GameStatus } from '../../constants/gameConstants';

const getUpdatedAtDateOnlyTime = (game) => {
  const updatedAt = game?.updatedAt;

  if (!updatedAt) return 0;

  let dateValue;
  if (updatedAt instanceof Date) {
    dateValue = updatedAt;
  } else if (typeof updatedAt?.toDate === 'function') {
    dateValue = updatedAt.toDate();
  } else if (typeof updatedAt?.seconds === 'number') {
    dateValue = new Date(updatedAt.seconds * 1000);
  } else if (typeof updatedAt?._seconds === 'number') {
    dateValue = new Date(updatedAt._seconds * 1000);
  } else {
    const parsed = Date.parse(updatedAt);
    dateValue = Number.isNaN(parsed) ? null : new Date(parsed);
  }

  if (!dateValue || Number.isNaN(dateValue.getTime())) return 0;

  return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()).getTime();
};

const getStatusRank = (status) => {
  if (status === GameStatus.NOT_STARTED || status === 'NOT_STARTED') return 0;
  if (status === GameStatus.COMPLETE || status === 'COMPLETE') return 2;
  return 1;
};

const getGreenRinkSortKey = (game) => {
  const green = `${game?.green ?? game?.greenRink?.match?.(/^([A-Z]+)\d+$/)?.[1] ?? ''}`.trim();
  const rink = `${game?.rink ?? game?.greenRink?.match?.(/^(?:[A-Z]+)(\d+)$/)?.[1] ?? ''}`.trim();

  if (!green && !rink) return '';

  return `${green}${rink}`;
};

const sortGamesForSection = (games = []) =>
  [...games].sort((a, b) => {
    const aGreenRink = getGreenRinkSortKey(a);
    const bGreenRink = getGreenRinkSortKey(b);

    if (aGreenRink !== bGreenRink) {
      return aGreenRink.localeCompare(bGreenRink, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    }

    const statusOrder = getStatusRank(a.status) - getStatusRank(b.status);
    if (statusOrder !== 0) return statusOrder;

    return getUpdatedAtDateOnlyTime(b) - getUpdatedAtDateOnlyTime(a);
  });

const Section = () => {
  const dispatch = useDispatch();
  const { currentUserProfile } = useAuth();
  const { eventId, sectionId } = useParams();
  const [section, setSection] = useState();
  const [event, setEvent] = useState();
  const [loading, setLoading] = useState();

  const sectionGames = useSelector((state) => state.game.games);
  const loadingGames = useSelector((state) => state.game.loading);

  const [filterRounds, setFilterRound] = useState('');
  const [filteredGames, setFilteredGames] = useState(sectionGames);

  useEffect(() => {
    setLoading(true);
    const docRef = doc(store, 'events', eventId);

    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const eventData = { id: docSnap.id, ...docSnap.data() };
        setEvent(eventData);
      } else {
        console.log('No such document!');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (event && event.sections && event.sections.length > 0) {
      const section = event.sections.find((section) => section.id === sectionId);
      setSection(section);

      dispatch(getGameBySectionId(sectionId));
    }
  }, [event, sectionId, dispatch]);

  useEffect(() => {
    let _fgames = sectionGames;

    if (filterRounds !== '') {
      _fgames = _fgames.filter((g) => g.round === filterRounds);
    }

    setFilteredGames(sortGamesForSection(_fgames));
  }, [filterRounds, sectionGames]);

  const clearFilters = () => {
    setFilterRound('');
    setFilteredGames(sortGamesForSection(sectionGames));
  };

  return section ? (
    <Box>
      <Link to={`/events/${eventId}`} style={{ textDecoration: 'none' }}>
        <MuiLink color="primary" underline="hover">
          {event.name}
        </MuiLink>
      </Link>
      <Typography variant="h4" gutterBottom>
        {section.name}
      </Typography>

      {loadingGames ? (
        <BSCircularProgress caption={'Loading Games...'} />
      ) : (
        <>
          <Box display={'flex'} alignItems={'center'} gap={2} flexWrap={'wrap'}>
            <FormControl sx={{ width: 200, my: 2 }}>
              <InputLabel id={'filterRoundsLabel'}>Filter Rounds</InputLabel>
              <Select
                labelId="filterRoundsLabel"
                value={filterRounds}
                onChange={(event) => setFilterRound(event.target.value)}>
                <MenuItem value={''}>None</MenuItem>
                {(() => {
                  let container = [];
                  for (let i = 0; i < event.gameFormat.rounds; i++) {
                    container.push(
                      <MenuItem key={i} value={i + 1}>
                        Round {i + 1}
                      </MenuItem>
                    );
                  }
                  return container;
                })()}
              </Select>
            </FormControl>
            <Button size="small" variant="text" color="primary" onClick={clearFilters}>
              Clear
            </Button>
          </Box>

          <Box display={'flex'} flexDirection={'row'} flexWrap={'wrap'} gap={2}>
            {filteredGames && filteredGames.length > 0 ? (
              filteredGames.map((game, index) => <GameByStatusCards game={game} key={index} />)
            ) : (
              <Typography>No Games</Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  ) : (
    <BSCircularProgress caption={'Loading Section...'} />
  );
};

export default Section;
